import { isEqual } from 'lodash';
import { NgxPermissionsService } from 'ngx-permissions';
import { firstValueFrom, map, takeUntil, withLatestFrom } from 'rxjs';
import {
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BaseApiClient } from '@ellf/app-api-client';
import {
  FacadeInterface,
  mapFromColumnState,
  mapFromFilterModel,
  SharedFacadeInterface,
} from '@ellf/app-common';
import { NgrxLoaderFacade } from '@ellf/app-ngrx-loader';
import { RouterService } from '@ellf/app-routing';
import { BaseDbDto } from '@ellf/common-dto';
import { FilterValue } from '@ellf/common-interfaces';
import { BaseListContainer } from './base-list.container';
import { loadPage } from './base-paged-list-action';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class BasePagedListContainer<T extends BaseDbDto>
  extends BaseListContainer<T>
  implements OnInit
{
  searchFilters: FilterValue[] = [];

  constructor(
    @Inject('SharedEntityFacade')
    sharedFacade: SharedFacadeInterface<T>,
    @Inject('EntityFacade')
    facade: FacadeInterface<T>,
    protected routerService: RouterService,
    permissionsService: NgxPermissionsService,
    protected apiClient: BaseApiClient<T>,
    protected loaderFacade: NgrxLoaderFacade,
    route?: ActivatedRouteSnapshot,
  ) {
    super(sharedFacade, facade, routerService, permissionsService, route);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.gridOptions$ = this.columnDefs$.pipe(
      withLatestFrom(this.pageSize$),
      map(([columnDefs, pageSize]) => {
        // this.columnDefs = columnDefs;

        const customGridOptions = {
          rowModelType: 'serverSide',
          serverSideInfiniteScroll: true, // al posto di serverSideStoreType dalla 28, true partial, false full
          // serverSideStoreType: 'partial',
          cacheBlockSize: pageSize,
          paginationPageSize: pageSize,
          onGridReady: (e: GridReadyEvent) => this.onGridReady(e),
        };
        return Object.assign(this.commonGridOptions, customGridOptions);
      }),
    );
  }

  onGridReady(event: GridReadyEvent) {
    super.onGridReady(event);

    this.pageSize$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((pageSize) => {
      setTimeout(() => {
        this.setDatasource(event.api, pageSize);
      }, 0);
    });
  }

  protected setDatasource(api: GridApi, pageSize: number) {
    api.setGridOption('serverSideDatasource', {
      destroy: () => {
        // console.log('destroy datasource');
      },
      getRows: async (params: IServerSideGetRowsParams) => {
        // Anti redux pattern
        try {
          this.loaderFacade.showLoader(loadPage);

          this.facade.setPageLoaded(false);

          const presetFilters = await firstValueFrom(this.presetFilters$);
          const fields = await firstValueFrom(this.fields$);

          const filters = mapFromFilterModel(
            (params.request.filterModel as any) || undefined,
            fields,
          );

          this.searchFilters.forEach((sf) => {
            const index = filters.findIndex((f) => f.field === sf.field);
            if (index >= 0) {
              filters[index] = sf;
            } else {
              filters.push(sf);
            }
          });

          presetFilters.forEach((sf) => {
            const index = filters.findIndex(
              (f) =>
                f.field === sf.field &&
                f.type === sf.type &&
                f.operator === sf.operator &&
                isEqual(f.value, sf.value),
            );
            if (index >= 0) {
              filters[index] = sf;
            } else {
              filters.push(sf);
            }
          });

          const orderBy = mapFromColumnState(params.request.sortModel);
          const skipFilterPermission = await firstValueFrom(
            this.skipFilterPermission$,
          );
          const result = await firstValueFrom(
            this.apiClient.getPaged(
              pageSize,
              params.request.startRow || 0,
              orderBy,
              filters,
              this.join,
              skipFilterPermission,
            ),
          );

          params.success({
            rowData: result.items,
            rowCount: result.totalCount,
          });

          this.sharedFacade.loadPagedSuccess(result.items);

          this.facade.setTotalRows(result.totalCount);
          this.facade.setPageLoaded(true);
          this.loaderFacade.hideLoader(loadPage);
        } catch (err) {
          this.loaderFacade.hideLoader(loadPage);
          this.sharedFacade.loadPagedFailure(err as any);
        }
      },
    });
  }
}
